/**
 * Acciones sobre la flota que los componentes disparan hacia el store.
 *
 * Viven fuera de React a propósito: son funciones puras de `(store) → api`, sin
 * estado ni hooks. Eso las hace de identidad estable para siempre, que es la
 * condición para que `<FleetProvider>` pueda exponerlas sin re-renderizar a sus
 * hijos, y de paso las deja testeables sin montar nada.
 */

import { api } from "../../api/client.js";
import { SYNC_QUEUE_THRESHOLD } from "../../config/syncAnimation.js";
import { evaluateSyncTrigger } from "./syncTrigger.js";

/**
 * @param {object} store FleetStore
 * @returns {{
 *   clearSyncRequest: (imei: string) => void,
 *   collapseTraceToNewest: (imei: string) => void,
 *   notifyQueueOverflow: (imei: string, payload?: object) => void,
 *   notifyCatchUp: (imei: string) => void,
 *   refreshDevices: () => void,
 * }}
 */
export function createFleetActions(store) {
  /** El marcador terminó su sync — la petición ya se puede retirar. */
  function clearSyncRequest(imei) {
    store.clearSyncRequest(imei);
  }

  /**
   * Colapsa el trail de un device a su punto más nuevo. Es el gesto que
   * acompaña a TODO sync: el marcador se pone al día sin dibujar el tramo, así
   * que la cola tampoco puede quedar pintada desde donde el usuario lo dejó.
   */
  function collapseTraceToNewest(imei) {
    store.updateTrace(imei, (arr) => (arr.length <= 1 ? arr : [arr[0]]));
  }

  /**
   * Lo llama VehicleMarker cuando su VehicleMotion emite `queueOverflow`
   * ({length, point, from}). Evalúa el trigger y escribe un syncRequest si el
   * gate de distancia pasa.
   *
   * Se prefiere el par point/from del payload sobre la traza del store: el
   * punto que desbordó la cola puede no haber llegado todavía a la traza, y
   * con el punto viejo el sync apuntaba al penúltimo mientras el colapso del
   * trail se quedaba con el más nuevo — el marcador aterrizaba un tramo detrás
   * de donde re-arrancaba el trail, y ese tramo quedaba en blanco para siempre
   * en un vehículo que acababa de estacionarse. Además el gate de distancia
   * medía un solo segmento en vez del atraso real ancla→punto.
   */
  function notifyQueueOverflow(imei, { length, point, from } = {}) {
    if (!store.getDevice(imei)) return;

    let prev = null;
    let next = null;
    if (
      Number.isFinite(from?.lat) &&
      Number.isFinite(from?.lng) &&
      Number.isFinite(point?.lat) &&
      Number.isFinite(point?.lng)
    ) {
      prev = { lat: from.lat, lng: from.lng, timestamp: from.ts ?? null };
      next = { lat: point.lat, lng: point.lng, timestamp: point.ts ?? null };
    } else {
      // Fallback (payload legacy sin point/from): los dos puntos más nuevos de
      // la traza, como antes.
      const tr = store.getTrace(imei);
      if (tr.length < 2) return;
      next = tr[0];
      prev = tr[1] ?? tr[0];
      if (
        !Number.isFinite(prev?.lat) ||
        !Number.isFinite(prev?.lng) ||
        !Number.isFinite(next?.lat) ||
        !Number.isFinite(next?.lng)
      )
        return;
    }
    const prevTs = prev.timestamp ? Date.parse(prev.timestamp) : NaN;
    const nextTs = next.timestamp ? Date.parse(next.timestamp) : NaN;
    const trigger = evaluateSyncTrigger({
      from: [prev.lat, prev.lng],
      to: [next.lat, next.lng],
      fromTs: Number.isFinite(prevTs) ? prevTs : null,
      toTs: Number.isFinite(nextTs) ? nextTs : null,
      absenceMs: null,
      queueLength: length ?? SYNC_QUEUE_THRESHOLD + 1,
    });
    if (!trigger) return;
    store.batch(() => {
      store.setSyncRequest(imei, {
        from: { lat: prev.lat, lng: prev.lng },
        to: { lat: next.lat, lng: next.lng },
        fromTs: Number.isFinite(prevTs) ? prev.timestamp : null,
        toTs: Number.isFinite(nextTs) ? next.timestamp : null,
        reason: trigger.reason,
      });
      // Sync disparado ⇒ el trail se colapsa al punto más nuevo, igual que en
      // los otros paths de sync (reconnect/tab-return, catch-up y live-gap): el
      // recorrido que la cola de animaciones iba a pintar no se dibuja.
      collapseTraceToNewest(imei);
    });
  }

  /**
   * Lo llama VehicleMarker cuando su VehicleMotion resuelve un catch-up (vuelta
   * a la pestaña tras una ausencia larga con salto real). El marcador ya se
   * está deslizando hasta el punto actual sin pintar nada; acá se colapsa el
   * trail para que tampoco quede pintado el tramo en la polilínea histórica.
   *
   * La decisión NO se toma acá: la traza avanza con cada mensaje WS aunque la
   * pestaña esté oculta, así que el store no sabe qué vio el usuario. El ancla
   * confirmada de VehicleMotion sí — por eso decide allá y acá se obedece.
   */
  function notifyCatchUp(imei) {
    collapseTraceToNewest(imei);
  }

  /**
   * Refresca SOLO la pertenencia a grupos (group_ids, group_names) sin tocar la
   * posición ni el estado en vivo que ya está en el store.
   */
  function refreshDevices() {
    return api
      .getDevices()
      .then((deviceList) => {
        store.batch(() => {
          for (const updated of deviceList) {
            store.patchDevice(updated.imei, {
              group_ids: updated.group_ids,
              group_names: updated.group_names,
            });
          }
        });
      })
      .catch((err) =>
        console.error("[fleetActions] refreshDevices failed:", err),
      );
  }

  return {
    clearSyncRequest,
    collapseTraceToNewest,
    notifyQueueOverflow,
    notifyCatchUp,
    refreshDevices,
  };
}

export default createFleetActions;
